import { msg, plural } from "@lingui/core/macro";
import React from "react";
import {
  PenaltiesInfographicStep,
  PenaltiesInfographicStepColor
} from "@silversea-ssc/ui-components-react";
import { Currency } from "src/utils/localization/currency";
import { formatI18nCurrency } from "src/utils/localization/formatCurrency";
import { AdministrativeFee } from "src/ssc-api/useRemotePaymentTerms";
import {
  CruiseGroupPaymentTerms,
  CruiseGroupV2ApiResponse
} from "@silversea-ssc/dotcom-api-types/CruiseGroup";
import { EnhancedCancellationPeriod } from "@silversea-ssc/dotcom-api-types/PaymentPeriods";
import { UseFetchDataResponse } from "src/ssc-api/useFetchApi";
import { FareCode } from "@silversea-ssc/web-fares";
import { useLingui } from "@lingui/react/macro";

const penaltyPercentageColor = (percentage: number) =>
  percentage === 0 ? "green" : percentage === 100 ? "red" : "yellow03";

// TODO: seems a duplicated function
const penaltyValue = (
  t: ReturnType<typeof useLingui>["t"],
  period: EnhancedCancellationPeriod,
  currency: Currency,
  cancellationAdminFee: AdministrativeFee | undefined
): { color: PenaltiesInfographicStepColor; label: JSX.Element } => {
  if (period.type === "Deposit")
    return {
      label: (
        <>
          Deposit
          <br />
          refundable*
        </>
      ),
      color: "yellow03"
    };

  const refundableLabel = t(msg`refundable`);
  const notLabel = t(msg`Not`);

  if (period.type == "NonRefundable") {
    return {
      label: buildLabel(notLabel, refundableLabel, cancellationAdminFee),
      color: "red"
    };
  }

  const penalty = period.penalty;

  switch (penalty.type) {
    case "percentage":
      return {
        color: penaltyPercentageColor(penalty.percentage),
        label: buildLabel(
          `${100 - penalty.percentage}%`,
          refundableLabel,
          cancellationAdminFee
        )
      };

    case "percentageWithMaximumAmount":
      return {
        color: penaltyPercentageColor(penalty.percentage),
        label:
          penalty.percentage == 100
            ? buildLabel(notLabel, refundableLabel, cancellationAdminFee)
            : buildLabel(
                `${100 - penalty.percentage}%`,
                `refundable up to ${formatI18nCurrency(
                  penalty.maxAmount,
                  currency
                )}`,
                cancellationAdminFee
              )
      };

    case "fixedAmountPerBooking":
      return {
        color: "green",
        label: buildLabel(
          formatI18nCurrency(penalty.amount, currency),
          `per booking`,
          cancellationAdminFee
        )
      };

    case "fixedAmountPerPerson":
      return {
        color: "green",
        label: buildLabel(
          formatI18nCurrency(penalty.amount, currency),
          `per person`,
          cancellationAdminFee
        )
      };
  }
};

const buildLabel = (
  prefix: string,
  suffix: string,
  cancellationAdminFee: AdministrativeFee | undefined
): JSX.Element => {
  return (
    <>
      <big>
        {prefix}
        {cancellationAdminFee?.amount ? "*" : ""}
      </big>{" "}
      {suffix}
    </>
  );
};

export const toPenaltiesInfographicStep =
  (
    t: ReturnType<typeof useLingui>["t"],
    currency: Currency,
    cancellationAdminFee: AdministrativeFee | undefined
  ) =>
  (
    period: EnhancedCancellationPeriod,
    index: number,
    array: readonly EnhancedCancellationPeriod[]
  ): React.ComponentProps<typeof PenaltiesInfographicStep> & {
    key: string;
  } => {
    const prevPeriod = array[index - 1] || {};

    const { validUntilDaysBeforeDeparture = undefined } = prevPeriod;
    const { color, label } =
      penaltyValue(
        t,
        period,
        currency,
        index == 0 ? cancellationAdminFee : undefined
      ) || {};

    return {
      key: index.toString(),
      dayLabel: validUntilDaysBeforeDeparture
        ? plural(validUntilDaysBeforeDeparture, {
            one: "# DAY",
            other: "# DAYS"
          })
        : undefined,
      color: color,
      label: label
    };
  };

export const formattedAdministrativeFeeDisclaimer = (
  t: ReturnType<typeof useLingui>["t"],
  value: AdministrativeFee | undefined,
  currency: Currency | undefined
): string | undefined => {
  if (!value || !currency) return undefined;

  const amount = formatI18nCurrency(value.amount, currency);
  if (value.amount <= 0) return undefined;

  switch (value.type) {
    case "fixedAmountPerBooking": {
      return t(
        msg({
          id: "administrative-fee-per-booking-disclaimer",
          message: `*Includes a ${amount} administrative fee per booking, which will be automatically converted to a Future Cruise Credit (FCC)`
        })
      );
    }

    case "fixedAmountPerPerson": {
      return t(
        msg({
          id: "administrative-fee-per-person-disclaimer",
          message: `*Includes a ${amount} administrative fee per person, which will be automatically converted to a Future Cruise Credit (FCC)`
        })
      );
    }
  }
};

export type RemotePaymentTermFareResponse<T> = T & { fareCode: FareCode };

export const fromCruiseGroupV2ApiResponse = (
  response: UseFetchDataResponse<CruiseGroupV2ApiResponse>
): (RemotePaymentTermFareResponse<CruiseGroupPaymentTerms> | undefined)[] =>
  response.data?.fares.map((f) => {
    if (!f.paymentTerms) return undefined;
    return {
      ...f.paymentTerms,
      fareCode: f.fareCode
    };
  }) || [];
